import React from 'react';
import { ChevronDown, ChevronUp, Trash2, Eye, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { getApiUrl, optionalAuthHeader } from "../../utils/api";
import { usePagination, useSortBy, useTable } from 'react-table';
import toast from "react-hot-toast";

export const EnrollmentTable = ({ fetchEnrollments, currentItems, handleDeleteClick, currentUser }) => {
    const apiUrl = getApiUrl();

    const resendForm = async (row) => {
        try {
            const requestOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...optionalAuthHeader(currentUser),
                },
                body: JSON.stringify({
                    id: row.id,
                    email: row.clientEmail,
                    firstName: row.clientFirstName,
                    lastName: row.clientLastName,
                }),
            };
            const response = await fetch(`${apiUrl}/enrollment/send`, requestOptions);
            if (response.ok) {
                await response.json();
                toast.success("Form successfully sent.");
                setTimeout(() => {
                    fetchEnrollments();
                }, 200);
            } else {
                toast.error("Failed to send form");
            }
        } catch (err) {
            toast.error("Failed to send form");
        }
    };

    const downloadPdf = async (id) => {
        try {
            window.open(`${apiUrl}/enrollment/downloadenrollmentpdf/${id}`, '_blank');
        } catch (err) {
            toast.error("Failed to send form");
        }
    };

    const columns = React.useMemo(() => {
        const baseColumns = [
            {
                Header: 'Company',
                accessor: 'policyName',
                Cell: ({ value }) => (
                    <span className="block max-w-[180px] truncate" title={value}>
                        {value}
                    </span>
                ),
            },
            {
                Header: 'Client',
                accessor: (row) => `${row.clientFirstName} ${row.clientLastName}`,
                id: 'client',
            },
            {
                Header: 'Email',
                accessor: 'clientEmail',
            },
            {
                Header: 'Status',
                accessor: (row) => {
                    const status = row.status.toLowerCase();
                    if (status === 'pending') {
                        return 'Unsigned';
                    } else if (status === 'complete') {
                        return 'Enrolled';
                    }
                    return status.charAt(0).toUpperCase() + status.slice(1);
                },
                id: 'status',
                Cell: ({ value }) => {
                    const statusColors = {
                        'Unsigned': 'bg-amber-50 text-amber-700 border-amber-200',
                        'Signed': 'bg-blue-50 text-blue-700 border-blue-200',
                        'Enrolled': 'bg-green-50 text-green-700 border-green-200',
                    };
                    return (
                        <span className={`inline-flex items-center justify-center min-w-[70px] px-2.5 py-1 rounded-full text-xs font-medium border ${statusColors[value] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                            {value}
                        </span>
                    );
                }
            },
            {
                Header: 'Created',
                accessor: 'createdAt',
            },
            {
                Header: 'Sent to Client',
                accessor: (row) => row.lastSent ? new Date(row.lastSent).toISOString().split('T')[0] : 'Unsent',
                id: 'lastSent',
            },
        ];

        if (currentItems.some(item => item.status === "pending")) {
            baseColumns.push({
                Header: 'Send Form',
                accessor: 'id',
                id: 'sendForm',
                Cell: ({ row }) => {
                    const lastSent = row.original.lastSent;
                    const status = row.original.status.toLowerCase();
                    if (status === 'pending') {
                        return (
                            <button
                                className="min-w-[72px] px-3 py-1.5 text-xs font-medium text-center text-success border border-success rounded-lg hover:bg-success hover:!text-white transition-colors"
                                onClick={() => resendForm(row.original)}
                            >
                                {lastSent ? 'Resend' : 'Send'}
                            </button>
                        );
                    }
                    return null;
                }
            });
        }

        if (currentItems.some(item => item.status === "complete" || item.status === "signed")) {
            baseColumns.push({
                Header: 'Signed',
                accessor: (row) => row.signDate ? new Date(row.signDate).toISOString().split('T')[0] : '',
                id: 'Signed',
                Cell: ({ value }) => (
                    <span className="inline-block min-w-[85px]">{value}</span>
                ),
            });
        }

        baseColumns.push({
            Header: 'Actions',
            accessor: 'id',
            id: 'view',
            Cell: ({ cell }) => {
                const id = cell.value;
                const item = currentItems.find(item => item.id === id);
                if (item) {
                    if (item.status === "pending") {
                        return (
                            <div className="flex items-center gap-2">
                                <a
                                    href={`/enroll/${id}?mode=edit`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 text-primary border border-primary rounded-lg hover:bg-primary hover:!text-white transition-colors"
                                    title="View"
                                >
                                    <Eye className="w-4 h-4" />
                                </a>
                                <button
                                    className="p-1.5 text-red-500 border border-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                                    onClick={() => handleDeleteClick(id)}
                                    title="Delete"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    } else if (item.status === "complete" || item.status === "signed") {
                        return (
                            <button
                                className="p-1.5 text-primary border border-primary rounded-lg hover:bg-primary hover:!text-white transition-colors"
                                onClick={() => downloadPdf(item.id)}
                                title="View PDF"
                            >
                                <Eye className="w-4 h-4" />
                            </button>
                        );
                    }
                }
                return null;
            },
        });

        return baseColumns;
    }, [currentItems, handleDeleteClick]);

    const data = React.useMemo(() => currentItems.filter(item => item.id), [currentItems]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0 },
        },
        useSortBy,
        usePagination
    );

    const getStatusBadge = (status) => {
        const statusColors = {
            'Unsigned': 'bg-amber-50 text-amber-700 border-amber-200',
            'Signed': 'bg-blue-50 text-blue-700 border-blue-200',
            'Enrolled': 'bg-green-50 text-green-700 border-green-200',
        };
        let displayStatus = status?.toLowerCase();
        if (displayStatus === 'pending') displayStatus = 'Unsigned';
        else if (displayStatus === 'complete') displayStatus = 'Enrolled';
        else displayStatus = status?.charAt(0).toUpperCase() + status?.slice(1);

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusColors[displayStatus] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                {displayStatus}
            </span>
        );
    };

    const renderMobileCard = (item) => {
        const clientName = `${item.clientFirstName} ${item.clientLastName}`;
        const lastSent = item.lastSent ? new Date(item.lastSent).toISOString().split('T')[0] : 'Unsent';
        const signDate = item.signDate ? new Date(item.signDate).toISOString().split('T')[0] : null;
        const isPending = item.status?.toLowerCase() === 'pending';
        const isComplete = item.status?.toLowerCase() === 'complete' || item.status?.toLowerCase() === 'signed';

        return (
            <div key={item.id} className="bg-gray-50 rounded-xl p-4">
                <div className="flex justify-between items-start gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 truncate">{item.policyName}</h4>
                        <p className="text-sm text-gray-600">{clientName}</p>
                    </div>
                    {getStatusBadge(item.status)}
                </div>

                <div className="text-sm text-gray-500 space-y-1 mb-3">
                    <p className="truncate">{item.clientEmail}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                        <span>Created: {item.createdAt}</span>
                        <span>Sent: {lastSent}</span>
                        {signDate && <span>Signed: {signDate}</span>}
                    </div>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                    {isPending && (
                        <>
                            <button
                                className="flex-1 px-3 py-2 text-xs font-medium text-center text-success border border-success rounded-lg hover:bg-success hover:!text-white transition-colors"
                                onClick={() => resendForm(item)}
                            >
                                {item.lastSent ? 'Resend' : 'Send'}
                            </button>
                            <a
                                href={`/enroll/${item.id}?mode=edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-primary border border-primary rounded-lg hover:bg-primary hover:!text-white transition-colors"
                                title="View"
                            >
                                <Eye className="w-4 h-4" />
                            </a>
                            <button
                                className="p-2 text-red-500 border border-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                                onClick={() => handleDeleteClick(item.id)}
                                title="Delete"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </>
                    )}
                    {isComplete && (
                        <button
                            className="flex-1 px-3 py-2 text-xs font-medium text-center text-primary border border-primary rounded-lg hover:bg-primary hover:!text-white transition-colors inline-flex items-center justify-center gap-2"
                            onClick={() => downloadPdf(item.id)}
                        >
                            <Eye className="w-4 h-4" />
                            View PDF
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Desktop table */}
            <div className="hidden md:block">
                <table {...getTableProps()} className="w-full table-fixed text-sm">
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-gray-100">
                                {headerGroup.headers.map(column => (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 transition-colors"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {column.render('Header')}
                                            {column.isSorted && (
                                                column.isSortedDesc
                                                    ? <ChevronDown className="w-3 h-3" />
                                                    : <ChevronUp className="w-3 h-3" />
                                            )}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()} className="divide-y divide-gray-50">
                        {page.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()} key={row.id} className="hover:bg-gray-50 transition-colors">
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()} className="py-3 px-2 text-gray-700">
                                            <div className='min-w-0 break-words'>
                                                {cell.render('Cell')}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
                {page.map(row => {
                    prepareRow(row);
                    return renderMobileCard(row.original);
                })}
            </div>

            {/* pagination */}
            <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => gotoPage(0)}
                        disabled={!canPreviousPage}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="First page"
                    >
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={previousPage}
                        disabled={!canPreviousPage}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Previous page"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={nextPage}
                        disabled={!canNextPage}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Next page"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => gotoPage(pageOptions.length - 1)}
                        disabled={!canNextPage}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Last page"
                    >
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>
                        Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                    </span>
                    <span className="flex items-center gap-2">
                        Go to:
                        <input
                            type="number"
                            min={1}
                            max={pageOptions.length}
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(pageNumber);
                            }}
                            className="w-14 px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                        />
                    </span>
                </div>

                <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                >
                    {[10, 20, 30, 40].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
